/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, useId, useState, type ReactElement } from 'react';
import { Await, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Link } from '@/components/link';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDownIcon, MinusIcon, PlusIcon } from '@/components/icons';
import MenuHref from '@/components/main-navigation/menu-href';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { cn } from '@/lib/utils';
import type { FooterBlock, FooterContent, FooterCopyrightSegment, FooterImageLink } from '@/lib/footer/types';
import logo from '/images/logo.svg';

export type FooterContentSource = FooterContent | Promise<FooterContent> | undefined;

const TITLE_CLASS = 'text-sm font-medium uppercase leading-[22px] lg:font-semibold';

function BlockTitle({ title }: { title?: string }): ReactElement | null {
    return title ? <h2 className={TITLE_CLASS}>{title}</h2> : null;
}

function ImageLinks({ links, variant }: { links: FooterImageLink[]; variant: 'icon' | 'badge' }): ReactElement {
    const config = useConfig();
    return (
        <ul className="flex gap-2">
            {links.map((link) => {
                const image = (
                    <img
                        src={toImageUrl({ src: link.src, config, options: { format: 'png' } }) ?? link.src}
                        alt={link.alt}
                        loading="lazy"
                        className={cn(
                            'block',
                            variant === 'icon'
                                ? 'size-[30px] rounded-full object-contain'
                                : 'h-6 w-20 object-contain object-left lg:w-full'
                        )}
                    />
                );
                return (
                    <li
                        key={`${link.href ?? ''}|${link.src}`}
                        className={variant === 'badge' ? 'lg:min-w-0 lg:flex-1' : undefined}>
                        {link.href ? (
                            <MenuHref
                                link={{ label: link.alt, href: link.href, external: link.external }}
                                {...(link.external && { target: '_blank' })}
                                className="block">
                                {image}
                            </MenuHref>
                        ) : (
                            image
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

function LinkList({ links, id, className }: { links: FooterBlock['links']; id?: string; className?: string }) {
    return (
        <ul id={id} className={cn('flex flex-col gap-2 lg:gap-0', className)}>
            {links.map((link) => (
                <li key={`${link.label}|${link.href}`} className="lg:py-1">
                    <MenuHref link={link} className="text-sm leading-[22px] hover:underline lg:leading-[18px]">
                        {link.label}
                    </MenuHref>
                </li>
            ))}
        </ul>
    );
}

/** A footer column: optional title, text, link list and image links */
function FooterColumnBlock({ block, imageVariant }: { block: FooterBlock; imageVariant: 'icon' | 'badge' }) {
    return (
        <div className="flex flex-col gap-2">
            <BlockTitle title={block.title} />
            {block.paragraphs.map((text) => (
                <p key={text} className="text-xs leading-5">
                    {text}
                </p>
            ))}
            {block.links.length > 0 ? <LinkList links={block.links} /> : null}
            {block.imageLinks.length > 0 ? <ImageLinks links={block.imageLinks} variant={imageVariant} /> : null}
        </div>
    );
}

function FooterAccordionBlock({ block }: { block: FooterBlock }): ReactElement {
    const [open, setOpen] = useState(false);
    const listId = useId();
    if (!block.title) return <FooterColumnBlock block={block} imageVariant="icon" />;
    return (
        <div className="flex flex-col gap-2 max-lg:border-t max-lg:border-footer-section-divider max-lg:pt-4">
            <div className="relative flex items-center gap-2">
                <div className="flex-1">
                    <BlockTitle title={block.title} />
                </div>
                <button
                    type="button"
                    aria-label={block.title}
                    aria-expanded={open}
                    aria-controls={listId}
                    onClick={() => setOpen((value) => !value)}
                    className="shrink-0 cursor-pointer before:absolute before:inset-0 lg:hidden">
                    {open ? <MinusIcon className="size-5" /> : <PlusIcon className="size-5" />}
                </button>
            </div>
            <LinkList links={block.links} id={listId} className={cn(!open && 'max-lg:hidden')} />
        </div>
    );
}

function Copyright({ segments }: { segments?: FooterCopyrightSegment[] }): ReactElement {
    const { t } = useTranslation('footer');
    return (
        <p className="text-[10px] leading-4 lg:text-center lg:text-xs">
            {segments
                ? segments.map((segment) =>
                      segment.type === 'link' ? (
                          <MenuHref key={`${segment.label}|${segment.href}`} link={segment} className="hover:underline">
                              {segment.label}
                          </MenuHref>
                      ) : (
                          segment.text
                      )
                  )
                : t('defaultCopyright', { year: new Date().getFullYear() })}
        </p>
    );
}

/**
 * Price Shoes site footer: a rounded primary card with a
 * centered chevron toggle, four columns (logo + text, shop, help, social + app) and an always
 * visible copyright line. Starts collapsed on every page load; the columns stay in the HTML while
 * collapsed (`forceMount` + `hidden`) so their links remain crawlable.
 */
function FooterView({ content }: { content: FooterContent }): ReactElement {
    const { t } = useTranslation('footer');
    const [open, setOpen] = useState(false);
    const contentId = useId();
    const { about, shop, help, social, app } = content;

    return (
        <footer className="mt-auto bg-footer-page-background lg:p-8">
            <Collapsible
                open={open}
                onOpenChange={setOpen}
                className="mx-auto bg-footer-background px-4 text-footer-foreground lg:rounded-3xl lg:px-8">
                <div className={cn('flex justify-center pt-4', open && 'lg:pt-6')}>
                    <CollapsibleTrigger
                        aria-controls={contentId}
                        aria-label={open ? t('toggle.collapse') : t('toggle.expand')}
                        className="cursor-pointer rounded-sm p-1 focus-visible:outline-2 focus-visible:outline-footer-foreground">
                        <ChevronDownIcon
                            aria-hidden="true"
                            className={cn('size-3.5 transition-transform', !open && 'rotate-180')}
                        />
                    </CollapsibleTrigger>
                </div>

                <CollapsibleContent forceMount id={contentId} hidden={!open}>
                    <div className="grid grid-cols-1 gap-4 pt-4 lg:grid-cols-[repeat(4,248px)] lg:justify-center lg:gap-10 lg:pt-6">
                        <div className="flex flex-col gap-2">
                            <Link to="/" className="self-start" data-testid="footer-logo">
                                <img
                                    src={logo}
                                    alt={t('logoAlt')}
                                    width={90}
                                    height={57}
                                    className="h-12 w-20 lg:h-[57px] lg:w-[90px]"
                                />
                            </Link>
                            {about?.paragraphs.map((text) => (
                                <p key={text} className="text-xs leading-5 max-lg:hidden">
                                    {text}
                                </p>
                            ))}
                        </div>
                        {shop ? <FooterAccordionBlock block={shop} /> : null}
                        {help ? (
                            <div className="max-lg:border-b max-lg:border-footer-section-divider max-lg:pb-4">
                                <FooterAccordionBlock block={help} />
                            </div>
                        ) : null}
                        {social || app ? (
                            <div className="flex flex-col gap-6 max-lg:mt-2 lg:gap-4">
                                {social ? <FooterColumnBlock block={social} imageVariant="icon" /> : null}
                                {app ? <FooterColumnBlock block={app} imageVariant="badge" /> : null}
                            </div>
                        ) : null}
                    </div>
                    <hr className="mt-8 border-0 border-t-[0.5px] border-footer-divider max-lg:hidden" />
                </CollapsibleContent>
                <div className={cn('pb-6', open ? 'pt-6' : 'pt-4 lg:pt-2')}>
                    <Copyright segments={content.copyright} />
                </div>
            </Collapsible>
        </footer>
    );
}

/**
 * Main storefront footer. `content` is the footer data streamed by the `_app` loader (or already
 * resolved); while it is pending, the collapsed footer renders with the default copyright so the
 * reserved height matches the resolved collapsed state (no layout shift).
 */
export default function MainFooter({ content }: { content?: FooterContentSource }): ReactElement {
    const { pathname } = useLocation();
    if (!(content instanceof Promise)) {
        return <FooterView key={pathname} content={content ?? {}} />;
    }
    return (
        <Suspense fallback={<FooterView content={{}} />}>
            <Await resolve={content} errorElement={<FooterView key={pathname} content={{}} />}>
                {(resolved) => <FooterView key={pathname} content={resolved} />}
            </Await>
        </Suspense>
    );
}
